import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useEstimateStratigraphies } from '@/hooks/useEstimateStratigraphies';

/**
 * Riallineamento della riga di preventivo dopo aver salvato la parete che la
 * riga usa.
 *
 * Il prezzo della riga e` congelato al momento dell'aggiunta: modificare la
 * parete non lo muove, ed e` il motivo per cui prima bisognava eliminare la
 * riga e rimetterla. Qui si riallinea subito riusando lo stesso ricalcolo del
 * pulsante "Aggiorna Prezzi", che rilegge dal catalogo.
 *
 * MAI su un preventivo gia' uscito: riscrivere un inviato, un vinto, un perso o
 * un contrattualizzato vorrebbe dire cambiare un'offerta che il cliente ha gia'
 * ricevuto. In quel caso si avvisa e si spiega come allineare a mano.
 *
 * Vive qui e non dentro una pagina perche' serve a DUE configuratori — pareti e
 * controsoffitti — e una regola commerciale copiata in due punti e` una regola
 * che prima o poi diverge in due punti.
 *
 * @param estimateId preventivo da cui si e` arrivati. Passare `undefined`
 *   quando non si sta modificando per un preventivo: l'hook non carica nulla.
 */
export function useSyncEstimateRowAfterSave(estimateId?: string | null) {
  const navigate = useNavigate();
  const estimateRows = useEstimateStratigraphies(estimateId ?? undefined);

  /**
   * @param savedId id della stratigrafia appena salvata
   * @param rowId   id della riga di preventivo da riallineare
   */
  return async function syncEstimateRow(savedId: string | null, rowId: string) {
    if (!savedId || !estimateId) return;

    const { data: estimate } = await supabase
      .from('estimates')
      .select('status')
      .eq('id', estimateId)
      .maybeSingle();
    const stato = estimate?.status ?? 'draft';

    if (stato !== 'draft') {
      toast.warning(
        'Parete salvata, ma la riga di preventivo non è stata aggiornata.',
        { description: `Il preventivo è in stato "${stato}". Per allinearlo usa "Aggiorna Prezzi" sulla riga.` },
      );
      navigate(`/estimates/${estimateId}/manage`);
      return;
    }

    await estimateRows.updateStratigraphyPrices(rowId, savedId);
    navigate(`/estimates/${estimateId}/manage`);
  };
}
